import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  Paper,
  Alert,
  Button,
  Popover,
  Divider,
  Stack,
  Chip,
} from '@mui/material';
import { DataTable, StatusBadge, ExportButton, DateRangePicker, LoadingSpinner, type StatusType } from '../components/shared';
import { smsService } from '../services';
import { getApiErrorMessage } from '../lib/apiError';
import { REMINDER_STATUSES, REMINDER_STATUS_LABELS } from '../config/reminderStatuses';
import type { SmsLog } from '../services/smsService';
import { GridColDef } from '@mui/x-data-grid';
import { format } from 'date-fns';

export const SmsLogsPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState<SmsLog[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [status, setStatus] = useState<string>('');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);

  // Detail popover
  const [popoverAnchor, setPopoverAnchor] = useState<HTMLElement | null>(null);
  const [selectedLog, setSelectedLog] = useState<SmsLog | null>(null);

  useEffect(() => {
    loadLogs();
  }, [page, pageSize, status, startDate, endDate]);

  const loadLogs = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await smsService.getLogs({
        status: status || undefined,
        startDate: startDate?.toISOString(),
        endDate: endDate?.toISOString(),
        page: page + 1,
        limit: pageSize,
      });

      setLogs(response.data || []);
      setTotal(response.pagination?.total || 0);
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to load SMS logs'));
      console.error('Load logs error:', err);
    } finally {
      setLoading(false);
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'scheduledDate',
      headerName: 'Scheduled',
      width: 160,
      valueFormatter: (params) => {
        return params.value ? format(new Date(params.value), 'PPp') : 'N/A';
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 200,
      // A simulated row reaches Delivered exactly like a real one, so the
      // status alone cannot be trusted to mean a phone received anything.
      renderCell: (params) => (
        <Stack direction="row" spacing={0.75} alignItems="center">
          <StatusBadge status={params.value} />
          {params.row.simulated && (
            <Chip label="Simulated" size="small" color="warning" variant="outlined" />
          )}
        </Stack>
      ),
    },
    {
      field: 'recipientName',
      headerName: 'Caregiver',
      flex: 1,
      minWidth: 150,
      valueFormatter: (params) => params.value || '-',
    },
    {
      field: 'phoneNumber',
      headerName: 'Phone',
      width: 140,
      valueFormatter: (params) => params.value || '-',
    },
    {
      field: 'messageText',
      headerName: 'Message',
      flex: 2,
      minWidth: 200,
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'sentAt',
      headerName: 'Sent At',
      width: 160,
      valueFormatter: (params) => {
        return params.value ? format(new Date(params.value), 'PPp') : '-';
      },
    },
    {
      field: 'deliveredAt',
      headerName: 'Delivered At',
      width: 160,
      valueFormatter: (params) => {
        return params.value ? format(new Date(params.value), 'PPp') : '-';
      },
    },
    {
      field: 'errorMessage',
      // Also carries why a reminder was cancelled, which is not an error.
      headerName: 'Error / Reason',
      flex: 1,
      minWidth: 150,
      valueFormatter: (params) => params.value || '-',
    },
    {
      field: 'retryCount',
      headerName: 'Retries',
      width: 90,
      align: 'center',
    },
  ];

  // Prepare export data
  const exportData = logs.map((log) => ({
    'Scheduled Date': log.scheduledDate ? format(new Date(log.scheduledDate), 'PPp') : 'N/A',
    'Status': log.status,
    // Sits next to Status on purpose. A CSV leaves the app and gets forwarded,
    // so a Delivered row with no marker would read as a real send.
    'Simulated': log.simulated ? 'Yes' : 'No',
    'Caregiver': log.recipientName || '-',
    'Phone': log.phoneNumber || '-',
    'Message': log.messageText,
    'Sent At': log.sentAt ? format(new Date(log.sentAt), 'PPp') : '-',
    'Delivered At': log.deliveredAt ? format(new Date(log.deliveredAt), 'PPp') : '-',
    'Error': log.errorMessage || '-',
    'Retry Count': log.retryCount,
  }));

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" gutterBottom fontWeight={600}>
            SMS Logs
          </Typography>
        </Box>
        <ExportButton
          data={exportData}
          filename={`sms-logs-${format(new Date(), 'yyyy-MM-dd')}`}
          disabled={logs.length === 0}
          variant="contained"
        />
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Paper variant="outlined" sx={{ px: 2, py: 1.5, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: { xs: 'wrap', md: 'nowrap' }, alignItems: 'center' }}>
          <TextField
            select
            label="Status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            sx={{ minWidth: 150, flex: { xs: '1 1 100%', md: '1 1 auto' } }}
            size="small"
          >
            <MenuItem value="">All Statuses</MenuItem>
            {REMINDER_STATUSES.map((key) => (
              <MenuItem key={key} value={key}>
                {REMINDER_STATUS_LABELS[key]}
              </MenuItem>
            ))}
          </TextField>

          <DateRangePicker
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
            startLabel="Start Date"
            endLabel="End Date"
          />

          {(status || startDate || endDate) && (
            <Button
              size="small"
              onClick={() => { setStatus(''); setStartDate(null); setEndDate(null); }}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Clear
            </Button>
          )}
        </Box>
      </Paper>

      {/* Data Table */}
      {loading && logs.length === 0 ? (
        <LoadingSpinner message="Loading SMS logs..." />
      ) : logs.length === 0 ? (
        <Paper
          variant="outlined"
          sx={{
            p: 8,
            textAlign: 'center',
            bgcolor: 'background.default',
          }}
        >
          <Typography variant="h6" gutterBottom color="text.secondary">
            No SMS Logs Found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {status || startDate || endDate
              ? 'Try adjusting your filters'
              : 'SMS logs will appear here once reminders are sent'}
          </Typography>
        </Paper>
      ) : (
        <DataTable
          rows={logs}
          columns={columns}
          loading={loading}
          rowCount={total}
          paginationMode="server"
          onPaginationModelChange={(model) => {
            setPage(model.page);
            setPageSize(model.pageSize);
          }}
          pageSize={pageSize}
          pageSizeOptions={[10, 25, 50, 100]}
          autoHeight
          onRowClick={(params, event) => {
            setSelectedLog(params.row as SmsLog);
            setPopoverAnchor(event.currentTarget as HTMLElement);
          }}
        />
      )}

      {/* Detail Popover */}
      <Popover
        open={!!popoverAnchor}
        anchorEl={popoverAnchor}
        onClose={() => { setPopoverAnchor(null); setSelectedLog(null); }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        slotProps={{ paper: { sx: { maxWidth: 420, width: '100%' } } }}
      >
        {selectedLog && (
          <Box sx={{ p: 2.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
              <Typography variant="subtitle1" fontWeight={600}>SMS Detail</Typography>
              <StatusBadge status={selectedLog.status as StatusType} />
            </Box>
            <Divider sx={{ mb: 1.5 }} />

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="caption" color="text.secondary">Caregiver</Typography>
                <Typography variant="body2" fontWeight={500}>{selectedLog.recipientName || '-'}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="caption" color="text.secondary">Phone</Typography>
                <Typography variant="body2" fontWeight={500}>{selectedLog.phoneNumber || '-'}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="caption" color="text.secondary">Scheduled</Typography>
                <Typography variant="body2">{selectedLog.scheduledDate ? format(new Date(selectedLog.scheduledDate), 'PPp') : '-'}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="caption" color="text.secondary">Sent At</Typography>
                <Typography variant="body2">{selectedLog.sentAt ? format(new Date(selectedLog.sentAt), 'PPp') : '-'}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="caption" color="text.secondary">Delivered At</Typography>
                <Typography variant="body2">{selectedLog.deliveredAt ? format(new Date(selectedLog.deliveredAt), 'PPp') : '-'}</Typography>
              </Box>
            </Box>

            <Divider sx={{ my: 1.5 }} />

            <Typography variant="caption" color="text.secondary">Message</Typography>
            <Typography variant="body2" sx={{ mt: 0.5, p: 1.5, bgcolor: 'action.hover', borderRadius: 1, whiteSpace: 'pre-wrap', fontSize: '0.8rem' }}>
              {selectedLog.messageText}
            </Typography>

            {selectedLog.errorMessage && (
              <Alert severity="error" sx={{ mt: 1.5, fontSize: '0.8rem' }}>
                {selectedLog.errorMessage}
              </Alert>
            )}
          </Box>
        )}
      </Popover>
    </Box>
  );
};
